/* maaho-price.js - the made-to-measure price calculation, for the product page.

   This is the same arithmetic as the pricing function that charges the customer
   (app/maaho-pricing/extensions/pricing/src/price.ts). Two copies exist because
   one has to run in the browser and the other inside Shopify's checkout, and they
   cannot share a module across that boundary.

   They are kept honest by a test: app/.../tests/theme-price.test.js runs the same
   384 cases from the old shop's database through THIS file. If the two ever
   disagree, that test fails and names the product and size.

   Everything is computed in the customer's currency INCLUDING VAT - surcharges are
   added to the price already on the page. The size grid and maaho.base_price are
   the only figures stored excluding VAT.

   Prices are exact: no whole kroner, no one-decimal euros, no tidy 0,95 ending.
   The only rounding is to the currency's minor unit, and the markets have
   Shopify's own rounding switched off so the page and the checkout agree. The base
   is converted the way Shopify converts it - see marketPrice - so the page also
   agrees with the collection tile.

   No exports and no bundler: it hangs one object off window so the section can
   load it with a plain <script>. */

window.MaahoPrice = (function () {
  'use strict';

  /* Half-up, to the currency's minor unit - the smallest amount chargeable. */
  function roundMoney(amount, decimals) {
    var factor = Math.pow(10, decimals);
    return Math.floor(amount * factor + 0.5) / factor;
  }

  /* Denmark's rate, which every stored price carries. The shop's currency is DKK. */
  var SHOP_VAT_RATE = 0.25;

  /**
   * An ex-VAT DKK figure as the market price, reproducing Shopify's own conversion.
   *
   * Shopify does not reach a market price in one multiplication. It strips Danish
   * VAT to the øre, adds the destination's to the øre, and converts last - and it
   * skips both tax steps where the destination charges what the shop already
   * carries, as Sweden and Norway do at 25%. Three roundings, not one.
   *
   * The collection tile and the search results carry Shopify's figure, so the page
   * has to agree with it to the cent. shopify-migration/derive-shopify-conversion.py
   * establishes this rule against 1.210 prices Shopify quoted; it is the only one of
   * 48 candidate orderings that reproduces every one.
   */
  function marketPrice(baseExcludingVat, vatRate, rate) {
    /* In whole øre, and never in a double. 309,00 × 1,255 is exactly 387,795, which
       a binary double holds as 387,79499… - it then rounds DOWN and shows 51,98 for
       a bordløber Shopify prices at 51,99. Shopify works in decimals, so the halves
       have to survive, and integer øre is the only way to be sure they do. */
    var MILLIONTHS = 1000000;
    var oreExcludingVat = Math.round(baseExcludingVat * 100);

    // The tax-inclusive DKK price - what Shopify stores and converts from.
    var grossOre = roundToOre(oreExcludingVat * 125, 100);
    if (vatRate !== SHOP_VAT_RATE) {
      var netOre = roundToOre(grossOre * 100, 125);
      grossOre = roundToOre(netOre * Math.round((1 + vatRate) * MILLIONTHS), MILLIONTHS);
    }
    return roundToOre(grossOre * Math.round(rate * MILLIONTHS), MILLIONTHS) / 100;
  }

  /* numerator / denominator in whole øre, an exact half going UP. */
  function roundToOre(numerator, denominator) {
    return Math.floor((2 * numerator + denominator) / (2 * denominator));
  }

  /* The next grid step at or above value, or null if it is off the grid.
     Always up: 105 cm pays the 110 cm price. */
  function snapUp(value, steps) {
    for (var index = 0; index < steps.length; index++) {
      if (value <= steps[index]) return steps[index];
    }
    return null;
  }

  /* DKK, SEK, NOK and EUR all divide by 100. A zero-decimal currency (ISK, JPY)
     would need a case here before the shop could sell in it. */
  function decimalsFor(currencyCode) {
    return 2;
  }

  /**
   * What one chosen attribute adds. Also used to label each choice on the page,
   * which is why it is a function of its own: a '*' or a '&' surcharge changes
   * with the size, so the label cannot be a fixed number written in the markup.
   *
   * @param {Array}  entry     [operator, value] from option_prices.surcharges
   * @param {number} base      the size price, gross, in the shown currency
   * @param {number} width     the width TYPED, not the width snapped to the grid
   * @returns {number|null}    null for an operator we do not know
   */
  function optionAmount(entry, base, width, widthStep, money, second, tables, measures) {
    var decimals = money.decimals || 2;
    // Quoted gross in DKK, so reverse charge takes the Danish VAT out of them.
    // '*' needs no case, being a share of a base that is already net.
    var rate = money.vatExempt ? money.currencyRate / (1 + SHOP_VAT_RATE)
                               : money.currencyRate;
    var operator = entry[0];
    var value = entry[1];

    if (operator === '+') return roundMoney(value * rate, decimals);
    if (operator === '*') return roundMoney(base * value, decimals);   // share of the base
    if (operator === '&') {
      // A product with no grid asks for no measurement, so there is no width to
      // charge per. Null rather than zero: zero would give the option away.
      if (typeof width !== 'number' || !isFinite(width)) return null;
      return roundMoney((width / (widthStep || 20)) * value * rate, decimals);
    }
    if (operator === '#') {
      /* Framivo: a price looked up in a shared size table - value names it, e.g.
         ['#', 'mat'] for the passepartout table in shop.metafields.framivo.
         Cells are gross DKK like '+', read at a size snapped up to the table's
         next step. Which size: the table's own `measure` names a line property
         (the passepartout reads '_mat_opening', the window the customer typed),
         found in selection.measures; a table without one reads the frame's
         width x second. Off the table, or no measurements yet: null,
         so the attribute is never given away at a price nobody set. The
         checkout function must do the same before any product uses '#'. */
      var sizeTable = tables ? tables[value] : null;
      if (!sizeTable) return null;
      try {
        sizeTable = window.FramivoPriceTables.decodePriceTable(sizeTable);
      } catch (tableError) {
        return null;
      }
      var size = { width: width, second: second };
      if (sizeTable.measure) {
        size = measures ? measures[sizeTable.measure] : null;
        if (!size) return null;
      }
      if (typeof size.width !== 'number' || !isFinite(size.width)
          || typeof size.second !== 'number' || !isFinite(size.second)) return null;
      var tableWidth = snapUp(size.width, sizeTable.width.values);
      var tableSecond = snapUp(size.second, sizeTable.second.values);
      if (tableWidth === null || tableSecond === null) return null;
      var tableRow = sizeTable.prices[sizeTable.second.values.indexOf(tableSecond)];
      var tableCell = tableRow ? tableRow[sizeTable.width.values.indexOf(tableWidth)] : null;
      if (typeof tableCell !== 'number' || !isFinite(tableCell) || tableCell <= 0) return null;
      return roundMoney(tableCell * rate, decimals);
    }
    return null;
  }

  /**
   * A size grid is not required. 31 products come in one size at one price and are
   * configured only by their options: they pass no table, and their base comes from
   * maaho.base_price instead of a grid cell. Both are ex VAT in DKK, so VAT and the
   * currency are applied in one place for both kinds of product.
   *
   * @param {object} table    maaho.price_table, or null for a product without one
   * @param {object} options  maaho.option_prices, or null
   * @param {object} selection {width, second, chosen: [attributeId],
   *                            shippingSurcharge, baseExcludingVat}
   * @param {object} money    {vatRate, currencyRate, decimals, vatExempt} - vatRate
   *                          is the DESTINATION's, currencyRate the market's fixed
   *                          rate, vatExempt a reverse-charge customer who pays no
   *                          VAT at all. Must match cart_transform_run's answer, or
   *                          the page quotes one price and the checkout charges another.
   * @returns {object|null}   {base, surcharges, shipping, total} or null when the
   *                          size cannot be made - never a guessed price.
   */
  /* Framivo: the size tables a '#' surcharge reads - the product's own if it
     carries any, otherwise the shop-wide ones the section publishes. */
  function sharedTables(options) {
    return (options && options.tables) || window.maahoSharedPriceTables || null;
  }

  function calculate(table, options, selection, money) {
    var decimals = money.decimals || 2;
    var rate = money.currencyRate;
    var baseExcludingVat = null;

    if (table) {
      try {
        table = window.FramivoPriceTables.decodePriceTable(table);
      } catch (tableError) {
        return null;
      }
      if (selection.width < table.width.min || selection.second < table.second.min) return null;

      var width = snapUp(selection.width, table.width.values);
      var second = snapUp(selection.second, table.second.values);
      if (width === null || second === null) return null;

      var row = table.prices[table.second.values.indexOf(second)];
      var cell = row ? row[table.width.values.indexOf(width)] : null;
      // A cell is a price only if it is a number above zero. A gap in a supplier's
      // sheet arrives as null, but 0, a blank and a stray string all mean the same
      // thing -- nobody set a price for this size. Must match the same test in
      // extensions/pricing/src/price.ts, or the page quotes what checkout refuses.
      if (typeof cell !== 'number' || !isFinite(cell) || cell <= 0) return null;

      baseExcludingVat = cell;
    } else if (typeof selection.baseExcludingVat === 'number'
               && isFinite(selection.baseExcludingVat)) {
      baseExcludingVat = selection.baseExcludingVat;
    } else {
      // No base means no price rather than a zero one, which would make every
      // '*' surcharge free and show the surcharges as the whole price.
      return null;
    }

    /* Both bases are ex VAT in DKK, so the VAT and the currency are applied here
       and nowhere else - the same way Shopify applies them, so this is the figure
       the collection tile already shows. */
    var base = marketPrice(baseExcludingVat, money.vatExempt ? 0 : money.vatRate, rate);

    var widthStep = (options && options.width_step) || 20;
    var surcharges = [];
    var chosen = selection.chosen || [];

    for (var index = 0; index < chosen.length; index++) {
      var entry = options && options.surcharges ? options.surcharges[chosen[index]] : null;
      if (!entry) continue;
      var amount = optionAmount(entry, base, selection.width, widthStep, money,
        selection.second, sharedTables(options), selection.measures);
      if (amount === null) {
        if (entry[0] === '#') return null;
        continue;
      }
      surcharges.push({ attributeId: chosen[index], operator: entry[0], amount: amount });
    }

    var total = base;
    for (var i = 0; i < surcharges.length; i++) total += surcharges[i].amount;
    var shipping = roundMoney((selection.shippingSurcharge || 0)
                              * (money.vatExempt ? rate / (1 + SHOP_VAT_RATE) : rate),
                              decimals);
    /* Every part is already at the currency's minor unit, so this only clears
       floating-point noise - 216,82 + 40,10 lands on 256,91999... */
    total = roundMoney(total + shipping, 2);

    return { base: base, surcharges: surcharges, shipping: shipping, total: total };
  }

  return {
    calculate: calculate,
    optionAmount: optionAmount,
    sharedTables: sharedTables,
    snapUp: snapUp,
    decimalsFor: decimalsFor
  };
})();
